/*
 * Feeds newsletter signups into the Meta pixel's Advanced Matching.
 *
 * Why this is needed: the Shopify Forms app renders its fields inside an open
 * shadow root (<shopify-forms-embed>). Meta's Automatic Advanced Matching
 * scrapes form fields with a light-DOM querySelectorAll, which does not pierce
 * shadow DOM — so it registers zero fields on the storefront (verified live:
 * `fbq.getState().pixels[0].userDataFormFields` is {} on the homepage but fully
 * populated on checkout, where the inputs sit in the light DOM). The signup
 * email was therefore never reaching Meta, leaving every pre-checkout event
 * anonymous (fbp + IP + UA only).
 *
 * On submit we hand the email/first name to fbq as manual advanced matching.
 * fbq SHA-256 hashes advanced matching values before transmission, so no
 * plaintext email leaves the browser. This is the same `em`/`fn` match keys the
 * pixel is already configured to collect at checkout — not a new data category.
 *
 * Values persist in localStorage so every later event, and later visits from
 * the same browser, carry the match data.
 */
(function () {
  'use strict';

  if (window.Shopify && window.Shopify.designMode) return;

  // Checkout is Shopify-hosted and does not render theme.liquid, so this should
  // never execute there. Guarded anyway: on checkout the real identity comes
  // from the customer's own entries (Automatic Advanced Matching reads those
  // fields directly, and CAPI sends the order's true email), and a stale
  // newsletter address from a shared browser must never compete with it.
  if (/\/checkouts?(\/|$)/.test(window.location.pathname)) return;

  var STORE_KEY = 'meta-am';

  var store;
  try {
    store = window.localStorage;
    store.setItem('meta-am:test', '1');
    store.removeItem('meta-am:test');
  } catch (e) {
    return;
  }

  function read() {
    try {
      return JSON.parse(store.getItem(STORE_KEY)) || null;
    } catch (e) {
      return null;
    }
  }

  // Meta expects advanced matching values trimmed and lowercased; it hashes them.
  function normalizeEmail(value) {
    var email = String(value || '').trim().toLowerCase();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email) ? email : null;
  }

  function normalizeName(value) {
    return String(value || '').trim().toLowerCase().slice(0, 60) || null;
  }

  // Re-calling init on an already-initialized pixel merges advanced matching
  // data without firing an event (verified live: userData filled, eventCount
  // unchanged). The pixel id is read from live state so it can never drift from
  // whatever the Meta sales channel initialized.
  function apply(data, attempt) {
    attempt = attempt || 0;
    var state = typeof window.fbq === 'function' && window.fbq.getState && window.fbq.getState();
    var pixel = state && state.pixels && state.pixels[0];

    if (!pixel || !pixel.id) {
      if (attempt < 40) setTimeout(function () { apply(data, attempt + 1); }, 500);
      return;
    }

    var userData = {};
    if (data.em) userData.em = data.em;
    if (data.fn) userData.fn = data.fn;
    if (!userData.em && !userData.fn) return;

    window.fbq('init', pixel.id, userData);
  }

  function capture(root) {
    var emailField = root.getElementById
      ? root.getElementById('email')
      : root.querySelector('#email');
    var nameField = root.getElementById
      ? root.getElementById('first_name')
      : root.querySelector('#first_name');

    var email = normalizeEmail(emailField && emailField.value);
    if (!email) return;

    var data = { em: email };
    var first = normalizeName(nameField && nameField.value);
    if (first) data.fn = first;

    var existing = read();
    if (existing && existing.em === data.em && existing.fn === data.fn) return;

    try {
      store.setItem(STORE_KEY, JSON.stringify(data));
    } catch (e) {
      // still apply for this page view even if persistence fails
    }

    apply(data);
  }

  // The app submits via JS, so a native 'submit' event is not guaranteed —
  // listen for both, and read the field values at the moment of the action.
  function watch(embed) {
    var root = embed.shadowRoot;
    if (!root || root.metaAmBound) return false;
    root.metaAmBound = true;

    root.addEventListener('submit', function () { capture(root); }, true);
    root.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        var button = target && target.closest && target.closest('button');
        if (button && (!button.type || button.type === 'submit')) capture(root);
      },
      true
    );
    // Fallback: Enter-to-submit inside the email field.
    root.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Enter') capture(root);
      },
      true
    );

    return true;
  }

  // attachShadow does not trigger MutationObserver, so embeds whose shadow root
  // has not been attached yet are polled briefly after they appear.
  var pending = new Set();
  var timer = null;

  function drain() {
    pending.forEach(function (embed) {
      if (!embed.isConnected || watch(embed)) pending.delete(embed);
    });
    if (pending.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  }

  function track(embed) {
    if (watch(embed)) return;
    pending.add(embed);
    if (!timer) timer = setInterval(drain, 100);
  }

  function scan(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.tagName === 'SHOPIFY-FORMS-EMBED') track(node);
    node.querySelectorAll('shopify-forms-embed').forEach(track);
  }

  scan(document.documentElement);

  // Popup and flyout forms mount long after page load, so keep watching.
  new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(scan);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Returning visitor who signed up earlier — apply as early as possible so
  // this page view's events carry the match data.
  var known = read();
  if (known) apply(known);
})();
